/**
 * CX-G02 live artifact-reduction engine (heuristic, assistive).
 * Attenuates samples inside detected artifact intervals; never overwrites
 * the source buffer (returns a new array). Raw EEG remains available.
 */

export const ARTIFACT_REDUCTION_MODEL_CARD_URI =
  "qms://model-card/artifact-reduction-heuristic-v1";

export const ARTIFACT_REDUCTION_ALGORITHM_ID =
  "artifact_reduction_heuristic_v1";

export type ReductionInterval = {
  type: string;
  channel?: string | null;
  startSeconds: number;
  endSeconds: number;
  confidence?: number;
};

export type ReduceChannelInput = {
  name: string;
  samples: ArrayLike<number>;
};

export type ReduceChannelOutput = {
  name: string;
  samples: number[];
  beforeTraceHash: string;
  afterTraceHash: string;
  intervalsApplied: number;
};

const TYPE_ALIASES: Record<string, string[]> = {
  eye: ["eye", "eog"],
  muscle: ["muscle", "emg"],
  electrode_pop: ["electrode_pop", "electrode", "pop"],
  flatline: ["flatline", "disconnection"],
  high_impedance: ["high_impedance", "disconnection"],
  movement: ["movement"],
  noise: ["noise", "line_noise"],
};

function normalizeType(type: string): string {
  return String(type || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

export function reductionTypeMatches(
  selected: string[],
  artifactType: string,
): boolean {
  const artifact = normalizeType(artifactType);
  return selected.some((sel) => {
    const key = normalizeType(sel);
    const aliases = TYPE_ALIASES[key] || [key];
    return aliases.some((alias) => artifact === alias || artifact.includes(alias));
  });
}

/** FNV-1a 32-bit hex over sample magnitudes (stable, non-crypto). */
export function hashTraceSamples(samples: ArrayLike<number>): string {
  let hash = 0x811c9dc5;
  const len = samples.length;
  const step = Math.max(1, Math.floor(len / 2048));
  for (let i = 0; i < len; i += step) {
    const quantized = Math.round((Number(samples[i]) || 0) * 100);
    hash ^= quantized & 0xff;
    hash = Math.imul(hash, 0x01000193);
    hash ^= (quantized >>> 8) & 0xff;
    hash = Math.imul(hash, 0x01000193);
  }
  hash ^= len;
  return (hash >>> 0).toString(16).padStart(8, "0");
}

/**
 * Soft-attenuate samples inside matching intervals (display-only reduction).
 * Default attenuation keeps a residual so reviewers see a modified-not-blank view.
 */
export function reduceChannelSamples(input: {
  samples: ArrayLike<number>;
  sampleRate: number;
  windowStartSeconds?: number;
  intervals: ReductionInterval[];
  reducedTypes: string[];
  channelName?: string;
  attenuation?: number;
}): {
  samples: number[];
  beforeTraceHash: string;
  afterTraceHash: string;
  intervalsApplied: number;
} {
  const rate = input.sampleRate > 0 ? input.sampleRate : 256;
  const windowStart = input.windowStartSeconds ?? 0;
  const attenuation =
    typeof input.attenuation === "number" && Number.isFinite(input.attenuation)
      ? Math.min(1, Math.max(0, input.attenuation))
      : 0.15;
  const before = Array.from(input.samples, (v) => Number(v) || 0);
  const beforeTraceHash = hashTraceSamples(before);
  const after = before.slice();
  let intervalsApplied = 0;

  for (const interval of input.intervals) {
    if (!reductionTypeMatches(input.reducedTypes, interval.type)) continue;
    if (
      interval.channel &&
      input.channelName &&
      interval.channel.toLowerCase() !== input.channelName.toLowerCase() &&
      interval.channel !== "*"
    ) {
      continue;
    }
    const startIdx = Math.max(
      0,
      Math.floor((interval.startSeconds - windowStart) * rate),
    );
    const endIdx = Math.min(
      after.length,
      Math.ceil((interval.endSeconds - windowStart) * rate),
    );
    if (endIdx <= startIdx) continue;
    intervalsApplied += 1;
    for (let i = startIdx; i < endIdx; i += 1) {
      after[i] = after[i] * attenuation;
    }
  }

  return {
    samples: after,
    beforeTraceHash,
    afterTraceHash: hashTraceSamples(after),
    intervalsApplied,
  };
}

export function reduceChannels(input: {
  channels: ReduceChannelInput[];
  sampleRate: number;
  windowStartSeconds?: number;
  intervals: ReductionInterval[];
  reducedTypes: string[];
  attenuation?: number;
}): {
  channels: ReduceChannelOutput[];
  algorithmId: typeof ARTIFACT_REDUCTION_ALGORITHM_ID;
  modelCardUri: typeof ARTIFACT_REDUCTION_MODEL_CARD_URI;
  assistive: true;
} {
  const channels = input.channels.map((channel) => {
    const result = reduceChannelSamples({
      samples: channel.samples,
      sampleRate: input.sampleRate,
      windowStartSeconds: input.windowStartSeconds,
      intervals: input.intervals,
      reducedTypes: input.reducedTypes,
      channelName: channel.name,
      attenuation: input.attenuation,
    });
    return {
      name: channel.name,
      samples: result.samples,
      beforeTraceHash: result.beforeTraceHash,
      afterTraceHash: result.afterTraceHash,
      intervalsApplied: result.intervalsApplied,
    };
  });
  return {
    channels,
    algorithmId: ARTIFACT_REDUCTION_ALGORITHM_ID,
    modelCardUri: ARTIFACT_REDUCTION_MODEL_CARD_URI,
    assistive: true,
  };
}
